<template>
  <div>
    <CwePageHeader :authenticationRequired="true" />

    <div id="RedeemInvitePage" class="shared-page">
      <div class="centeredContentBox">
        <div class="back-section">
          <div class="back-section-inner">
            <a v-if="!phoneVerified && !invalidInvite && !registrationFormShown && phoneWasMissing == true"
               @click="back()">
              <font-awesome-icon icon="fa-solid fa-angle-left"></font-awesome-icon>
            </a>
          </div>
        </div>

        <h3 class="bottomSpaceDouble header-left" v-show="initiated">{{ GetPageTitle() }}</h3>

        <div v-if="pageErrorMessage">
          <p class="alertBox textLarge bottomSpaceDouble">{{ pageErrorMessage }}</p>
        </div>

        <!-- Mode: Phone Validation 2FA -->
        <div v-if="pageMode=='validatePhone'">
          <p class="textLarge bottomSpace">{{ $t('redeem_invite_validate_phone_instructions', [formattedMaskedPhone]) }}</p>

          <form id="ValidatePhoneForm" @submit="ValidatePhonePin">
            <div class="section">
              <div>
                <input v-model="phonePin" maxlength="6" type="text" placeholder="xxxxxx" data-testid="phonePin" />
              </div>
            </div>

            <p @click="resendInvite" class="textLarge bottomSpaceDouble" v-html="$t('redeem_invite_validate_phone_didnt_receive', ['<a href=\'#\'>', '</a>']).replace(',','')"></p>

            <div class="buttonWrap">
              <button class="button" type="submit" value="ValidatePhonePin" data-testid="submitButton">{{ $t('redeem_invite_validate_phone_button') }}</button>
            </div>
          </form>
        </div>

        <!-- Mode: newOrExistingUserSelector, with Login With Existing Account or Register New Account buttons -->
        <div v-if="pageMode=='newOrExistingUserSelector'">

            <p class="textLarge bottomSpaceDouble alertBox"
               v-if="redeemInviteUserTypeMode=='migrating'" v-html="$t('redeem_invite_body_migrated_migrate')">
            </p>

            <div class="buttonWrap bottomSpaceDouble" v-if="redeemInviteUserTypeMode=='migrating' && allowRegister">
                <a @click="Register()" data-testid="registerLink">
                    <button class="button" type="button" value="Register" data-testid="registerButton">{{ $t('redeem_invite_register_migrated_button') }}</button>
                </a>
            </div>

            <p class="textLarge bottomSpaceDouble"
               v-if="redeemInviteUserTypeMode=='migrating' && allowRegister" v-html="$t('redeem_invite_body_migrated_login')">
            </p>

            <p class="textLarge bottomSpaceDouble"
               v-if="redeemInviteUserTypeMode=='optivu'">
                {{ $t('redeem_invite_body_optivu') }}
            </p>

            <p class="textLarge bottomSpaceDouble"
               v-if="redeemInviteUserTypeMode=='zbemployees'">
                {{ $t('redeem_invite_body_zb_employees') }}
            </p>

            <p class="textLarge bottomSpaceDouble"
               v-if="redeemInviteUserTypeMode=='federatedOnlyEmail'">
                {{ $t('redeem_invite_body_federated_email') }}
            </p>

            <p class="textLarge bottomSpaceDouble"
               v-if="redeemInviteUserTypeMode=='softMatch'"
               v-html="$t('redeem_invite_existing_account_suggest').includes('{0}')
                 ? $t('redeem_invite_existing_account_suggest', [suggestedAccountEmail])
                 : $t('redeem_invite_existing_account_suggest')">
            </p>

            <p class="textLarge bottomSpaceDouble"
               v-if="redeemInviteUserTypeMode=='default'">
                {{ $t('redeem_invite_body_login') }}
            </p>

            <div class="buttonWrap bottomSpaceDouble">
                <a :href="Login(true)" data-testid="loginLink">
                    <button class="button" type="button" value="Login" data-testid="loginButton">{{ $t('redeem_invite_login_button') }}</button>
                </a>
            </div>

            <p class="textLarge bottomSpaceDouble"
               v-if="(redeemInviteUserTypeMode=='default' || redeemInviteUserTypeMode=='softMatch') && allowRegister">
                {{ $t('redeem_invite_body_register') }}
            </p>

            <div class="buttonWrap" v-if="redeemInviteUserTypeMode!='migrating' && allowRegister">
                <a @click="Register()">
                    <button :class="redeemInviteUserTypeMode=='default' && existingAccountSuggested ? 'button-secondary' : 'button'"
                            type="button" value="Register" data-testid="registerButton">
                        {{ $t('redeem_invite_register_button') }}
                    </button>
                </a>
            </div>

        </div>

        <!-- Mode: registerNewAccount, with conditional password and phone fields -->
        <div v-if="pageMode=='registerNewAccount'">

          <p class="textLarge bottomSpaceDouble" v-if="!isFederatedUser">{{ $t('redeem_invite_register_body') }}</p>

          <form id="createAccountForm" @submit="SubmitPasswordPhone">

            <!-- Only show the passwords section of this page for non-@zimmerbiomet.com email addresses, since they already have their corporate credentials -->
            <div class="section" v-if="!isFederatedUser">
            <div class="label-bold">
                <label for="newPassword">{{ $t('redeem_invite_password') }}</label>
            </div>

            <div>
                <password-input v-model:modelValue="password" input-id="newPassword" data-testid="newPassword" />
            </div>

            <div class="error" v-if="passwordError.length">{{ passwordError }}</div>
            </div>

            <div class="section" v-if="!isFederatedUser">
            <div class="label-bold">
                <label for="confirmPassword">{{ $t('redeem_invite_confirm_password') }}</label>
            </div>

            <div>
                <password-input v-model:modelValue="confirmPassword" input-id="confirmPassword" data-testid="confirmPassword"/>
            </div>

            <div class="error" v-if="confirmPasswordError.length">{{ confirmPasswordError }}</div>
            </div>

            <div v-if="maskedPhone == null || maskedPhone?.length == 0 || phoneFromRegistration == true" class="section bottomSpaceDouble">
            <PhoneNumberInput :phoneNumberLabel="$t('mobile_number')"
                                :noMatchingCountriesLabel="$t('no_matching_countries')"
                                :phoneCountryCodes="this.phoneCountryCodes"
                                :phoneCountryCode="this.selectedCountryCode"
                                :phoneNumber="this.phone"
                                @updatePhoneCountryCode="phoneCountryCodeChanged($event)"
                                @updatePhoneNumber="phoneNumberChanged($event)"
                                @updatePhoneNumberError="phoneNumberErrorChanged($event)" />

            <div class="error" v-if="phoneError.length">{{ phoneError }}</div>
            </div>

            <div class="button-section-right">
                <button class="button-right" type="submit" value="Save Password" data-testid="savePassword">{{ isFederatedUser ? $t('redeem_invite_validate_phone_button') : $t('redeem_invite_save_password_button') }}</button>

                <button class="button-right go-back" type="button" @click="backToRegisterOrLogin" data-testid="goBack"> {{ $t('go_back') }} </button>
            </div>
          </form>
        </div>

        <!-- Mode: proceedToLogin -->
        <div id="proceedToLoginWrap" class="button-section-right" v-if="pageMode=='proceedToLogin'">

            <p class="textLarge bottomSpaceDouble center">{{ $t('redeem_invite_register_success') }}</p>

            <a :href="Login(false)">
                <button class="button-center" type="button" value="Login" data-testid="proceedToLoginButton">{{ $t('redeem_invite_proceed_to_login_button') }}</button>
            </a>
        </div>

      </div>
    </div>
  </div>

</template>

<script lang="ts">
  import axios from 'axios';
  import * as Vue from 'vue';
  import { ApiRoutes } from '../api/ApiRoutes';
  import CwePageHeader from '../components/CwePageHeader.vue';
  import { IStandardResponse } from '../models/StandardResponse';
  import ErrorUrlFromStatusCode from '../services/ErrorUrlFromStatusCode';
  import FixProxyUrl from '../services/FixProxyUrl';
  import FormatPhoneNumber from '../services/FormatPhoneNumber';
  import WaitForAppDependencies from '../services/AppDependencyService';
  import { enterPage } from '../services/Readiness';
  import { store } from '../store/store';
  import PhoneNumberInput from '../components/PhoneNumberInput.vue';
  import PasswordInput from '../components/PasswordInput.vue';
  import { CountryCode, PhoneCountryCodes } from '@/types/phoneCountryCodes';
  import parsePhoneNumber, { CountryCode as CountryCodeEnum, validatePhoneNumberLength, ValidatePhoneNumberLengthResult } from 'libphonenumber-js';
  import { i18n } from '@/plugins/i18n'
  const $t = i18n.global.t;

  interface IErrorKeys {
    [key: string]: string | undefined;
  }

  var ErrorMessages: IErrorKeys = {
    error_invite_country_code_must_match: 'error_invite_country_code_must_match',
    error_invite_account_type_must_match: 'error_invite_account_type_must_match',
    error_invite_already_redeemed_for_client: 'error_invite_already_redeemed_for_client',
	validate_2fa_pin_invalid: 'validate_2fa_pin_invalid',
    no_apps_enabled_for_your_account: 'no_apps_enabled_for_your_account',
    redeem_invite_invalid: 'redeem_invite_invalid',
	redeem_invite_invalidated: 'redeem_invite_invalidated'
  };

  interface IGetInviteResponse extends IStandardResponse {
    inviteFound: boolean;
    alreadyMatchesUser: boolean;
    alreadyRedeemed: boolean;
    phoneVerified: boolean;
    maskedPhone: string;
    phoneCountryCode: string;
    countryCode: string;
    email: string;
    noAppsEnabled: boolean;
    migratingSourceAppUser: boolean;
    isOptivuTenantUser: boolean;
    phoneFromRegistration: boolean;
    hasZimmerEmail: boolean;
    hasFederatedOnlyEmail: boolean;
    existingAccountSuggested: boolean;
    suggestedAccountEmail: string;
  }

  interface IRegisterInviteResponse extends IStandardResponse {
    inviteFound: boolean;
    alreadyRedeemed: boolean;
    phoneNotVerified: boolean;
	registrationBlocked: boolean;
  }

  interface RedeemInviteData {
    pageMode: string;
    inviteId: string | undefined;
    inviteCode: string | undefined;
    countryCode: string | undefined;
    phonePin: string | undefined;
    phoneVerified: boolean;
    invalidInvite: boolean;
    initiated: boolean;
    alreadyMatchesUser: boolean;
    noAppsEnabled: boolean;
    pageErrorMessage: string | undefined;
    password: string;
    confirmPassword: string;
    passwordError: string;
    confirmPasswordError: string;
    showProceedToLogin: boolean;
    registrationFormShown: boolean;
    registrationSuccessShown: boolean;
    maskedPhone: string;
    phone: string;
    phoneError: string;
    phoneCountryCodes: CountryCode[],
    phoneCountryCode: string,
    selectedCountryCode: CountryCode | undefined,
    formattedMaskedPhone: string;
    hasZimmerEmail: boolean;
    hasFederatedOnlyEmail: boolean;
    federatedTenantRequiredForZBEmployees: boolean;
    migratingSourceAppUser: boolean;
    isOptivuTenantUser: boolean;
    isFederatedUser: boolean;
    redeemInviteUserTypeMode: string;
    allowRegister: boolean;
    phoneOptional: boolean;
    phoneWasMissing: boolean;
    phoneFromRegistration: boolean;
    existingAccountSuggested: boolean;
    suggestedAccountEmail: string;
    smartTicket: string;
    redirecting: boolean;
  }

  export default Vue.defineComponent({
    name: 'RedeemInvitePage',
    components: {
      CwePageHeader,
      PhoneNumberInput,
      PasswordInput
    },
    data(): RedeemInviteData {
      return {
        pageMode: 'initializing',
        inviteId: '',
        inviteCode: '',
        countryCode: '',
        phonePin: '',
        phoneVerified: false,
        invalidInvite: false,
        initiated: false,
        alreadyMatchesUser: false,
        noAppsEnabled: false,
        pageErrorMessage: undefined,
        password: '',
        confirmPassword: '',
        passwordError: '',
        confirmPasswordError: '',
        showProceedToLogin: false,
        registrationFormShown: false,
        registrationSuccessShown: false,
        maskedPhone: '',
        phone: '',
        phoneError: '',
        phoneCountryCodes: PhoneCountryCodes.sort((a, b) => a.Code > b.Code ? 1 : b.Code > a.Code ? -1 : 0),
        phoneCountryCode: '',
        selectedCountryCode: undefined as CountryCode | undefined, 
        formattedMaskedPhone: '',
        hasZimmerEmail: false,
        hasFederatedOnlyEmail: false,
        federatedTenantRequiredForZBEmployees: false,
        migratingSourceAppUser: false,
        isOptivuTenantUser: false,
        isFederatedUser: false,
        redeemInviteUserTypeMode: '',
        allowRegister: false,
        phoneOptional: false,
        phoneWasMissing: false,
        phoneFromRegistration: false,
        existingAccountSuggested: false,
        suggestedAccountEmail: '',
        smartTicket: '',
        redirecting: false
      }
    },
    methods: {
      setPageMode: function() {
        // Leaving the page: show no form (the loading overlay stays up) until the browser navigates away.
        if (this.redirecting)
          this.pageMode = 'redirecting';
        else if(!this.initiated)
			this.pageMode = 'initializing';
        else if(this.invalidInvite)  
          // This mode doesn't have it's own screen, only populates the error message with the other screens hidden. 
			this.pageMode = 'invalidateInvite';
		else if (this.registrationSuccessShown)
			this.pageMode = 'proceedToLogin';
		else if (!this.phoneVerified && ((this.maskedPhone && !this.phoneFromRegistration) || (this.phone && this.phoneFromRegistration)) && !this.registrationFormShown)
			this.pageMode = 'validatePhone';
		else if ((this.phoneVerified || !this.phone) && !this.registrationFormShown)
          this.pageMode = 'newOrExistingUserSelector';
		else if (this.registrationFormShown)
	      this.pageMode = 'registerNewAccount';
      },
      back: function () {
        if (!this.phoneVerified && !this.invalidInvite && !this.registrationFormShown && this.phoneFromRegistration) {
          this.clearErrors();
          this.password = '';
          this.confirmPassword = '';
          this.phoneWasMissing = false;
          this.registrationFormShown = true;
        }
      },
      phoneCountryCodeChanged(countryCode: CountryCode) {
        this.phoneCountryCode = countryCode.Ext;
        this.selectedCountryCode = countryCode;
      },
      phoneNumberChanged(phone: any) {
        this.phone = phone;
      },
      phoneNumberErrorChanged(error: any) {
        this.phoneError = error;
      },
      GetPageTitle() {
        if (!this.phoneVerified && !this.noAppsEnabled && this.phoneOptional == true)
            return $t('redeem_invite_title_confirm');

        else if (this.redeemInviteUserTypeMode == 'migrating')
          return $t('redeem_invite_register_migrated_button');
        
        return $t('redeem_invite_title_activate');
      },
	  backToRegisterOrLogin: function () {
        this.registrationFormShown = false;
        if (this.phoneFromRegistration) 
            this.phone = '';
        this.setPageMode();
      },
      ValidatePhonePin: async function (e: any) {
        e.preventDefault();

        this.clearErrors();

        this.phonePin = this.phonePin?.trim();
                
        if (this.phonePin == null || this.phonePin.length < 6) {
		      this.pageErrorMessage = $t('validate_2fa_pin_invalid');
          return;
        }

        if (this.phoneWasMissing == false)
          this.LoadInvite(this.inviteId, this.inviteCode, this.phonePin, false, true);
        else
          this.RegisterInvite();
      },
      resendInvite: function (e: any) {
        e.preventDefault();
        if (e.target.tagName.toLowerCase() == 'a')
          this.LoadInvite(this.inviteId, this.inviteCode, undefined, true, true);
      },
      Login(withInviteParams: boolean) {
        let loginURL = '/login?';
        if (withInviteParams)
          loginURL += `inviteId=${this.inviteId}&inviteCode=${this.inviteCode}`;
        loginURL += `&countryCode=${this.countryCode}&locale=${store.state.browserLocale}`;
        return FixProxyUrl(loginURL);
      },
      Register() {
        this.registrationFormShown = true;
        this.setPageMode();
      },
      SubmitPasswordPhone: async function (e: any) {
        e.preventDefault();

        this.phone = this.phone?.trim();

        this.clearErrors();

		if (!this.isFederatedUser) {
            this.confirmPassword = this.confirmPassword.trim();
            this.password = this.password.trim();
            if (this.password.length < store.state.passwordMinLength)
                this.passwordError = $t('error_password_insufficient_length', [store.state.passwordMinLength.toString()]);
            else if (!this.containsRequiredCharacters(this.password))
                this.passwordError = $t('error_password_insufficient_complexity');

            if (this.password != this.confirmPassword)
                this.confirmPasswordError = $t('error_passwords_dont_match');
        }

        // Ensure if the user selects a country, they have to enter a phone number
        if (this.phoneCountryCode?.length == 0 && !this.selectedCountryCode && this.phone?.length > 0)
          this.phoneError = $t('invalid_phone_number');
        // Ensure if the user enters a phone number, they have to select a country
        else if (this.phone?.length == 0 && this.phoneCountryCode?.length > 0 && this.selectedCountryCode)
          this.phoneError = $t('invalid_phone_number');

        if (this.selectedCountryCode && this.phone) {
          this.phoneWasMissing = true;
          this.formattedMaskedPhone = FormatPhoneNumber(this.phone, this.phoneCountryCode);

          var countryCodeEnum = this.selectedCountryCode.Code as CountryCodeEnum;
          var parsedPhoneNumber = parsePhoneNumber("+" + this.selectedCountryCode.Ext + this.phone, countryCodeEnum);

          if (parsedPhoneNumber) {
            var validPhoheNumber = parsedPhoneNumber.isValid();
            var validPhoneNumberLength = validatePhoneNumberLength("+" + this.selectedCountryCode.Ext + this.phone, countryCodeEnum) as ValidatePhoneNumberLengthResult;

            if (validPhoheNumber == false || validPhoneNumberLength == 'TOO_LONG')
              this.phoneError = $t('invalid_phone_number');
          }
        }

        if (this.confirmPasswordError?.length == 0 && this.passwordError?.length == 0 && this.phoneError?.length == 0) {
          this.RegisterInvite();
        }
      },
      clearErrors: function () {
        this.passwordError = '';
        this.phoneError = '';
        this.confirmPasswordError = '';
        this.pageErrorMessage = '';
      },
      containsRequiredCharacters: function (p: string) {
        var anUpperCase = /[A-Z]/;
        var aLowerCase = /[a-z]/;
        var aNumber = /[0-9]/;
        var aSpecial = /[!|@|#|$|%|^|&|*|(|)|-|_]/;

        var numUpper = 0;
        var numLower = 0;
        var numNums = 0;
        var numSpecials = 0;

        for (var i = 0; i < p.length; i++) {
          if (anUpperCase.test(p[i]))
            numUpper++;
          else if (aLowerCase.test(p[i]))
            numLower++;
          else if (aNumber.test(p[i]))
            numNums++;
          else if (aSpecial.test(p[i]))
            numSpecials++;
        }

        if (numUpper < 1 || numLower < 1 || numNums < 1) // || numSpecials < 1)
          return false;

        return true;
      },
      LoadInvite(inviteId: string | undefined, inviteCode: string | undefined, phonePin: string | undefined, refreshPin: boolean, clearErrors: boolean) {
        this.$store.state.loadingCount++;

        if (clearErrors)
          this.clearErrors();

        return axios.get(ApiRoutes.User.GetInvite, { params: { id: inviteId, code: inviteCode, phonePin: phonePin, refreshPin: refreshPin } }).then((response) => {
          const inviteResponse = response?.data as IGetInviteResponse;

          // SMART launch: the page only verifies the invite phone; the doorman completes the redemption.
          if (this.smartTicket && inviteResponse.phoneVerified) {
            this.redirectTo(FixProxyUrl('/fhir/r4/doorman?t=' + encodeURIComponent(this.smartTicket)));
            return;
          }

          this.populateDataFromInvite(inviteResponse);
        })
          .catch((error) => {
            const inviteResponse = error.response?.data as IGetInviteResponse;

            if (error?.response?.status == 422 && inviteResponse?.errors?.length > 0) {
              this.invalidInvite = !inviteResponse.inviteFound || inviteResponse.alreadyRedeemed;
              this.pageErrorMessage = inviteResponse.errors[0].localizedName;
              this.populateDataFromInvite(inviteResponse);
            } else {
              this.redirectTo(ErrorUrlFromStatusCode(error?.response?.status));
            }
          })
          .finally(() => {
            // Navigating away: keep the overlay up rather than re-rendering stale state until the page unloads.
            if (this.redirecting)
              return;
			this.$store.state.loadingCount--;
			this.setPageMode();
          });
      },
      redirectTo(url: string) {
        this.redirecting = true;
        this.setPageMode();
        window.location.href = url;
      },
      RegisterInvite() {
        this.$store.state.loadingCount++;

        var requestParams = {
          InviteId: this.inviteId,
          InviteCode: this.inviteCode,
          Password: this.password,
          Phone: this.phone,
          PhoneCountryCode: this.phoneCountryCode,
          PhonePin: this.phoneWasMissing ? this.phonePin : null
        }

        axios.post(ApiRoutes.User.RegisterInvite, requestParams).then((response) => {

          const inviteResponse = response.data as IRegisterInviteResponse;

          this.registrationFormShown = true;
          this.registrationSuccessShown = true;
          this.showProceedToLogin = true;
        })
        .catch((error) => {
          const inviteResponse = error.response?.data as IRegisterInviteResponse;

          if ((error?.response?.status == 422 || error?.response?.status == 400) && inviteResponse?.errors?.length > 0) {
              this.invalidInvite = !inviteResponse.inviteFound;

			// This scenario is when there was a federated user who needed to enter their phone number, then after verifying gets routed to the activate via login flow 
			if (inviteResponse.registrationBlocked && !inviteResponse.phoneNotVerified) {
				this.registrationFormShown = false;
                this.phoneVerified = true;
                return;
			}

            this.pageErrorMessage = inviteResponse.errors[0].localizedName;
                    
            if (inviteResponse.phoneNotVerified) {
              this.phoneVerified = false;
              this.registrationFormShown = false;                  

              if (this.phoneWasMissing) {
                this.pageErrorMessage = '';
              }
            }

            if (inviteResponse.alreadyRedeemed)
              this.showProceedToLogin = true;
		    } else {
              window.location.href = ErrorUrlFromStatusCode(error?.response?.status);
            }
        })
        .finally(() => {
          this.$store.state.loadingCount--;
	      this.setPageMode();
        });
      },
      populateDataFromInvite(inviteResponse: IGetInviteResponse) {
        this.phoneVerified = inviteResponse.phoneVerified;
        this.countryCode = inviteResponse.countryCode;
        this.maskedPhone = inviteResponse.maskedPhone;
        this.phoneCountryCode = inviteResponse.phoneCountryCode;
        this.formattedMaskedPhone = FormatPhoneNumber(this.maskedPhone, this.phoneCountryCode);
        this.hasZimmerEmail = inviteResponse.hasZimmerEmail;
        this.hasFederatedOnlyEmail = inviteResponse.hasFederatedOnlyEmail;
        this.migratingSourceAppUser = inviteResponse.migratingSourceAppUser;
        this.isOptivuTenantUser = inviteResponse.isOptivuTenantUser;
        this.noAppsEnabled = inviteResponse.noAppsEnabled;
        this.phoneOptional = inviteResponse.maskedPhone?.length == 0;
        this.phoneFromRegistration = inviteResponse.phoneFromRegistration;
        this.alreadyMatchesUser = inviteResponse.alreadyMatchesUser;
        this.existingAccountSuggested = inviteResponse.existingAccountSuggested;
        this.suggestedAccountEmail = inviteResponse.suggestedAccountEmail ?? '';

        if(this.noAppsEnabled)
          this.invalidInvite = true;

        if (inviteResponse.alreadyRedeemed)
          this.showProceedToLogin = true;

        this.allowRegister = false;
        // optivu users and zb emails and already registered emails can't register a new account, only login with their existing accounts
        if(this.isOptivuTenantUser) {
		  this.redeemInviteUserTypeMode = 'optivu';
		  this.isFederatedUser = true;
        } else if(this.federatedTenantRequiredForZBEmployees && this.hasZimmerEmail){
          this.redeemInviteUserTypeMode = 'zbemployees';
          this.isFederatedUser = true;
        } else if (this.hasFederatedOnlyEmail) {
          this.redeemInviteUserTypeMode = 'federatedOnlyEmail';
          this.isFederatedUser = true;
        } else if (this.existingAccountSuggested && !this.alreadyMatchesUser) {
          this.redeemInviteUserTypeMode = 'softMatch';
          if (!this.alreadyMatchesUser) this.allowRegister = true;
        } else if (this.migratingSourceAppUser && !this.alreadyMatchesUser) {
          this.redeemInviteUserTypeMode = 'migrating';
          this.allowRegister = true;
        }else{ 
          this.redeemInviteUserTypeMode = 'default';
          if(!this.alreadyMatchesUser) this.allowRegister = true;
        }

		this.initiated = true;
		this.setPageMode();
      }
    },
    async mounted() {
      const page = enterPage('redeem-invite');
      await WaitForAppDependencies();

      this.federatedTenantRequiredForZBEmployees = store.state.federatedTenantRequiredForZBEmployees;

      let errorKey = this.$route.query.errorKey?.toString() ?? '';
      errorKey = errorKey.trim().toLowerCase();

      if (errorKey != null && ErrorMessages[errorKey]) {
        this.pageErrorMessage = $t(errorKey);
      }

      // A SMART launch arrives with the doorman's one-time ticket in the URL fragment (never sent to the server);
      // capture it and drop the fragment from the address bar.
      if (this.$route.query.smart?.toString().toLowerCase() === 'true') {
        this.smartTicket = new URLSearchParams(window.location.hash.substring(1)).get('t') ?? '';
        window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
      }

      this.inviteId = this.$route.query.id?.toString();
      this.inviteCode = this.$route.query.code?.toString();
      this.phonePin = this.$route.query.phonePin?.toString();

      if (!this.inviteId)
        this.inviteId = '0';
      this.inviteId = (this.inviteId + '').replace(/[^a-z0-9]/gi, '').trim();


      await this.LoadInvite(this.inviteId, this.inviteCode, this.phonePin, false, false);
      // Ready in whichever mode the invite left the page — unless it is leaving (a redirect bypasses the router).
      if (!this.redirecting)
        page.ready();
    }
  })
</script>

<style lang="scss">
  .buttonWrap {
    margin-bottom: $standard_space;
  }

  #RedeemInvitePage {
    margin-left: auto;
    margin-right: auto;
    padding-left:4%;
    padding-right:4%; 

    p {
      text-align: left;
    }

    .alertBox {
      text-align: center
    }

    .centeredContentBox {
      margin-left: auto;
      margin-right: auto;
    }

    .phoneNumberRow > .phoneCountryCodeColumn {
      min-width: 150px;
      max-width: 150px;
    }

    .multiselect__tags, #phoneNumberInput {
      background: $very_light_gray;
      border: 0px;
      border-radius: 6px;
    }

    .multiselect__single {
      background: $very_light_gray;
    }
  }

  #createAccountForm {
    margin: auto;
    width: 100%;
  }

  input, .passwordInput {
    width: 100%;
  }

</style>
